import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { cloneDeep } from 'lodash';
import { useGetLicenseDetailsQuery } from '../services/products.endpoints';
import { SupportedHostApps } from '../schemas/products.schemas';

interface AssignSupportedHostAppsProps {
  supportedHostApps: SupportedHostApps;
  changeSupportedHostApps: (supportedHostApps: SupportedHostApps) => void;
}

const AssignSupportedHostApps = ({
  supportedHostApps,
  changeSupportedHostApps,
}: AssignSupportedHostAppsProps) => {
  const { data: details, isLoading } = useGetLicenseDetailsQuery();
  const [basename, setBasename] = useState('');
  const [version, setVersion] = useState('');

  useEffect(() => {
    if (supportedHostApps.length > 0 && details) {
      setVersion(supportedHostApps[0].version);
      const newBasename = details.find(({ basename: bname }) =>
        supportedHostApps[0].id.includes(bname),
      );
      if (newBasename) {
        setBasename(newBasename.basename);
      }
    }
  }, [details, supportedHostApps]);

  if (!details) {
    return <CircularProgress />;
  }
  const basenameOptions = details.map((base) => base.basename);
  const { versions, ids } =
    details.find((base) => base.basename === basename) ?? {};

  return (
    <>
      <Grid item xs={6}>
        <TextField
          id="basename"
          label="Base Name in License Database"
          name="basename"
          required
          fullWidth
          select
          disabled={isLoading || supportedHostApps.length > 0}
          value={basename}
          onChange={(e) => setBasename(e.target.value)}
        >
          {basenameOptions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {versions && (
        <Grid item xs={6}>
          <TextField
            id="version"
            label="Version"
            name="version"
            required
            fullWidth
            select
            disabled={isLoading}
            value={version}
            onChange={(e) => {
              setVersion(e.target.value);
              const apps = cloneDeep(supportedHostApps);
              changeSupportedHostApps(
                apps.map((app) => ({
                  ...app,
                  version: e.target.value,
                })),
              );
            }}
          >
            {versions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="h5">Supported Host Apps</Typography>
      </Grid>
      {supportedHostApps.map(({ displayName, id }, i) => (
        <>
          <Grid item xs={5} key={`id-${id}`}>
            {ids && (
              <TextField
                id={`id-${i}`}
                label="ID in License Database"
                name="id"
                required
                fullWidth
                select
                disabled={isLoading}
                value={id}
                onChange={(e) => {
                  const apps = cloneDeep(supportedHostApps);
                  apps[i].id = e.target.value;
                  changeSupportedHostApps(apps);
                }}
              >
                {ids.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={5} key={`display-name-${id}`}>
            <TextField
              id={`displayName-${i}`}
              label="Display Name of Host App"
              name="displayName"
              required
              fullWidth
              placeholder="Avid"
              disabled={isLoading}
              value={displayName}
              onChange={(e) => {
                const apps = cloneDeep(supportedHostApps);
                apps[i].displayName = e.target.value;
                changeSupportedHostApps(apps);
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              onClick={() => {
                const apps = cloneDeep(supportedHostApps);
                apps.splice(i, 1);
                changeSupportedHostApps(apps);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </>
      ))}
      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => {
            const apps = cloneDeep(supportedHostApps);
            changeSupportedHostApps([
              ...apps,
              { displayName: '', id: '', version },
            ]);
          }}
        >
          Add a Host App
        </Button>
      </Grid>
    </>
  );
};

export default AssignSupportedHostApps;
